import { Adjustments, MaskGroupAdjustments, PointColorHSL, emptyPointColor } from "@/core/types"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { ColorPickerPipette } from "../ColorPicker/ColorPickerPipette"
import { Slider } from "../ui/Slider"
import { ColorSampleResult } from "@/core/color-sampler"
import { useEditor } from "@/utils/useEditor"
import clsx from "clsx"
import { showContextMenu } from "../ContextMenuManager"
import { ContextMenuItem } from "../ui/ContextMenu"
import {
	getDefaultRelativeShiftRange,
	getHSVRelativeShiftInRange,
	getHueBackground,
	getLumBackground,
	getSatBackground,
	shiftRGB,
} from "./utils"
import { RGBToHSV } from "@/core/utils/color-conversion"
import {
	PointColorHueMaxShift,
	PointColorLumMaxShift,
	PointColorSatMaxShift,
} from "@/core/hsl-point-color"
import { l2Distance } from "@/utils/math"
import { showToast } from "@/store/ui-views-state"
import { useAtomValue } from "jotai"
import { photoLoadingStateAtom } from "@/store/editor/photo-loading"
import { uuid } from "@/utils/uuid"
import { HSLColorPicker } from "./HSLColorPicker"
import { HardSoftRangeSlider } from "./HardSoftRangeSlider"
import { ControlGroup } from "../ControlGroup"
import { Icon } from "../icons/Icon"

// i18n-ignore-start: history message values, displayed via historyControlNameKeys
const PointColorNameMapping = {
	satShift: "Saturation Shift",
	lumShift: "Luminance Shift",
	hueShift: "Hue Shift",
	range: "Range",
	hueRange: "Hue Range",
	satRange: "Saturation Range",
	lumRange: "Luminance Range",
}
// i18n-ignore-end

export const HSLPointColor = (props: {
	values: Adjustments | MaskGroupAdjustments
	isForMask?: boolean
	onAddColor: (color: PointColorHSL) => void
	onRemoveColor: (color: PointColorHSL) => void
	onRemoveAllColors: () => void
	onChangeColor: (color: PointColorHSL) => void
	onEndChangingColor: (color: PointColorHSL, name: string, displayValue: any) => void
	onSliderDown?: () => void
	onSliderUp?: () => void
}) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const [selectedPointColor, setSelectedPointColor] = useState<PointColorHSL | null>(null)
	const photoLoadingState = useAtomValue(photoLoadingStateAtom)

	useEffect(() => {
		if (!selectedPointColor) {
			setSelectedPointColor(props.values.pointColors?.at(0) ?? null)
			return
		}

		const pointColor = props.values.pointColors?.find(
			(pointColor) => pointColor.id === selectedPointColor.id,
		)

		if (!pointColor) {
			setSelectedPointColor(null)
		} else {
			setSelectedPointColor(pointColor)
		}
	}, [props.values])

	useEffect(() => {
		if (photoLoadingState !== "loaded") {
			return
		}

		setSelectedPointColor(props.values.pointColors?.at(0) ?? null)
	}, [photoLoadingState])

	const handleAddColor = (result: ColorSampleResult) => {
		if (
			result.colorRelative[0] == 1 &&
			result.colorRelative[1] == 1 &&
			result.colorRelative[2] == 1
		) {
			showToast(t("hslPointColor.toastSelectMoreSaturated"))
			return false
		}

		if (props.values.pointColors?.length >= 10) {
			showToast(t("hslPointColor.toastMaxColorsReached"))
			return false
		}

		const hsv = RGBToHSV(result.colorRelative)

		if (hsv[1] < 5) {
			showToast(t("hslPointColor.toastSelectMoreSaturated"))
			return false
		}
		if (hsv[2] < 5) {
			showToast(t("hslPointColor.toastClickBrighterArea"))
			return false
		}

		const relativeHSV = getHSVRelativeShiftInRange(hsv)

		const pointColor: PointColorHSL = {
			id: uuid(),
			color: result.colorRelative as [number, number, number],
			hueShift: 0,
			satShift: 0,
			lumShift: 0,
			range: 0.5,
			hueRange: getDefaultRelativeShiftRange(relativeHSV[0]),
			satRange: getDefaultRelativeShiftRange(relativeHSV[1]),
			lumRange: getDefaultRelativeShiftRange(relativeHSV[2]),
		}

		const closestDistanceIndex =
			props.values.pointColors
				?.map((entry) => l2Distance(entry.color, result.colorRelative, 2))
				.findIndex((distance) => distance < 0.1) ?? -1

		if (closestDistanceIndex >= 0) {
			// Don't add colors that are very close to existing ones
			// This is coherent with Lightroom Classic behaviour
			setSelectedPointColor(props.values.pointColors[closestDistanceIndex] ?? null)
			return true
		}

		setSelectedPointColor(pointColor)

		props.onAddColor(pointColor)

		return true
	}

	const handleUpdateValue = (name: string, value: number) => {
		if (!selectedPointColor) {
			return
		}

		const updatedPointColor = {
			...selectedPointColor,
			[name]: value,
		}

		setSelectedPointColor(updatedPointColor)

		const selectedIndex = props.values.pointColors.findIndex(
			(value) => value.id === updatedPointColor.id,
		)

		if (selectedIndex < 0) {
			return
		}

		props.onChangeColor(updatedPointColor)
	}

	const handleCommitValue = (name: string, displayedValue: any) => {
		if (!selectedPointColor) {
			return
		}

		props.onEndChangingColor(selectedPointColor, PointColorNameMapping[name], displayedValue)
	}

	const handleUpdateColor = (updatedPointColor: PointColorHSL) => {
		if (!selectedPointColor) {
			return
		}

		setSelectedPointColor(updatedPointColor)

		props.onChangeColor(updatedPointColor)
	}

	const handleCommitColor = () => {
		if (!selectedPointColor) {
			return
		}

		props.onEndChangingColor(selectedPointColor, "Point Color Shifts", "Multiple") // i18n-ignore
	}

	const handleDeletePointColor = (pointColor: PointColorHSL) => {
		const selectedIndex = editor.adjustments.pointColors.findIndex(
			(value) => value.id === pointColor.id,
		)

		props.onRemoveColor(pointColor)

		if (selectedIndex < 0) {
			return
		}

		if (props.values.pointColors.length - 1 > 0 && selectedIndex > 0) {
			setSelectedPointColor(props.values.pointColors[selectedIndex - 1] ?? null)
		} else {
			setSelectedPointColor(null)
		}
	}

	const handleDeleteAllPointColors = () => {
		props.onRemoveAllColors()
	}

	const handleOpenPointColorMenu = (e: React.MouseEvent, pointColor: PointColorHSL) => {
		e.stopPropagation()

		showContextMenu(
			e,
			<div aria-label={t("hslPointColor.ariaPointColorSelectorMenu")}>
				<ContextMenuItem
					trackingLabel="point_color_delete_swatch"
					aria-label={t("hslPointColor.ariaDeletePointColorSwatch")}
					onSelect={() => handleDeletePointColor(pointColor)}
				>
					{t("hslPointColor.deleteSwatch")}
				</ContextMenuItem>
				<ContextMenuItem
					trackingLabel="point_color_delete_all-swatches"
					aria-label={t("hslPointColor.ariaDeleteAllPointColorSwatches")}
					onSelect={() => handleDeleteAllPointColors()}
				>
					{t("hslPointColor.deleteAllSwatches")}
				</ContextMenuItem>
			</div>,
		)
	}

	const getCurrentPointColorHSV = (): [number, number, number] => {
		const rgb = selectedPointColor?.color ?? [0, 0, 0]
		return RGBToHSV(rgb)
	}

	const getCurrentPointColorRelativeHSV = (): [number, number, number] => {
		const hsv = getCurrentPointColorHSV()

		if (!hsv) {
			return [0.5, 0.5, 0.5]
		}

		return getHSVRelativeShiftInRange(hsv)
	}

	const getOriginalPointColorString = (pointColor: PointColorHSL) => {
		return `rgb(${pointColor.color[0] * 255}, ${pointColor.color[1] * 255}, ${
			pointColor.color[2] * 255
		})`
	}

	const getShiftedPointColorString = (pointColor: PointColorHSL) => {
		const shiftedColor = shiftRGB(pointColor.color, pointColor)

		return `rgb(${shiftedColor[0] * 255}, ${shiftedColor[1] * 255}, ${shiftedColor[2] * 255})`
	}

	/* Rendering */

	const renderRangeSlider = () => {
		return (
			<Slider
				disabled={selectedPointColor === null}
				className="w-full"
				title={t("hslPointColor.sliderRange")}
				panelName={t("hslPointColor.panelPointColor")}
				focusKey="pointColorRange"
				compact={true}
				min={0}
				max={100}
				defaultValue={0.5}
				keyboardStep={1}
				shiftKeyboardStep={5}
				hoverKeyboardStep={1}
				hoverShiftKeyboardStep={10}
				value={selectedPointColor?.range ?? 0.5}
				onMouseDown={props.onSliderDown}
				onMouseUp={props.onSliderUp}
				onUpdate={(val) => handleUpdateValue("range", val)}
				onUpdateEnded={(_, displayedValue) => handleCommitValue("range", displayedValue)}
			/>
		)
	}

	const renderRangeSliderExpandView = (
		isExpanded: boolean | undefined,
		clickHandler: (e: React.MouseEvent) => void,
	) => {
		return (
			<Icon
				onClick={clickHandler}
				name="down"
				className="ml-[-5px] mt-[0.5px] opacity-100 transition-transform"
				style={{
					transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
				}}
				size={21}
			/>
		)
	}

	const renderColorSelector = (pointColor: PointColorHSL) => {
		return (
			<div
				onContextMenu={(e) => handleOpenPointColorMenu(e, pointColor)}
				onClick={() => setSelectedPointColor(pointColor)}
				key={pointColor.id}
				className={clsx(
					"relative size-5 overflow-hidden rounded-sm",
					pointColor.id == selectedPointColor?.id
						? "border border-white"
						: "border border-black",
				)}
			>
				<div
					className="absolute inset-0"
					style={{
						backgroundColor: getOriginalPointColorString(pointColor),
					}}
				></div>
				<div
					className="absolute inset-0 size-full"
					style={{
						backgroundColor: getShiftedPointColorString(pointColor),
						clipPath: "polygon(100vmax 100vmax, 100% 100%, 0% 100%, 100% 0%)",
					}}
				></div>
			</div>
		)
	}

	return (
		<div>
			{/* Pipette + swatch */}
			<div className="my-3 flex flex-row items-center">
				<div className="flex w-full flex-row items-center gap-2">
					{props.values.pointColors?.map((color) => {
						return renderColorSelector(color)
					})}
				</div>
				<ColorPickerPipette
					isForMask={props.isForMask}
					className="ml-auto"
					onSelected={handleAddColor}
					purpose="point-color"
				/>
			</div>
			<HSLColorPicker
				className="my-2"
				pointColor={selectedPointColor ?? emptyPointColor}
				onUpdateColor={(pointColor) => {
					handleUpdateColor(pointColor)
				}}
				onUpdateColorEnded={() => {
					handleCommitColor()
				}}
			/>
			{/* Preview */}
			<div className="flex h-5 w-full justify-center overflow-hidden rounded-sm border border-base-500">
				{/* Preview Original Color */}
				<div
					className="size-full bg-white/10 dark:bg-[rgb(60,60,60)]"
					style={{
						backgroundColor: selectedPointColor
							? getOriginalPointColorString(selectedPointColor)
							: undefined,
					}}
				></div>
				{/* Preview Shifted Color */}
				<div
					className="size-full bg-white/10 dark:bg-[rgb(60,60,60)]"
					style={{
						backgroundColor: selectedPointColor
							? getShiftedPointColorString(selectedPointColor)
							: undefined,
					}}
				></div>
			</div>
			<div className="mt-4">
				{/* Shift Sliders */}
				<Slider
					disabled={selectedPointColor === null}
					title={t("hslPointColor.sliderHueShift")}
					panelName={t("hslPointColor.panelPointColor")}
					focusKey="pointColorHueShift"
					background={getHueBackground(
						getCurrentPointColorHSV(),
						PointColorHueMaxShift,
						50,
					)}
					value={selectedPointColor?.hueShift ?? 0}
					onMouseDown={props.onSliderDown}
					onMouseUp={props.onSliderUp}
					keyboardStep={1}
					shiftKeyboardStep={5}
					hoverKeyboardStep={1}
					hoverShiftKeyboardStep={10}
					onUpdate={(val) => handleUpdateValue("hueShift", val)}
					onUpdateEnded={(_, displayedValue) =>
						handleCommitValue("hueShift", displayedValue)
					}
				/>
				<Slider
					disabled={selectedPointColor === null}
					title={t("hslPointColor.sliderSatShift")}
					panelName={t("hslPointColor.panelPointColor")}
					focusKey="pointColorSatShift"
					background={getSatBackground(
						getCurrentPointColorHSV(),
						PointColorSatMaxShift,
						50,
					)}
					value={selectedPointColor?.satShift ?? 0}
					onMouseDown={props.onSliderDown}
					onMouseUp={props.onSliderUp}
					keyboardStep={1}
					shiftKeyboardStep={5}
					hoverKeyboardStep={1}
					hoverShiftKeyboardStep={10}
					onUpdate={(val) => handleUpdateValue("satShift", val)}
					onUpdateEnded={(_, displayedValue) =>
						handleCommitValue("satShift", displayedValue)
					}
				/>
				<Slider
					disabled={selectedPointColor === null}
					title={t("hslPointColor.sliderLumShift")}
					panelName={t("hslPointColor.panelPointColor")}
					focusKey="pointColorLumShift"
					background={getLumBackground(getCurrentPointColorHSV(), PointColorLumMaxShift)}
					value={selectedPointColor?.lumShift ?? 0}
					onMouseDown={props.onSliderDown}
					onMouseUp={props.onSliderUp}
					keyboardStep={1}
					shiftKeyboardStep={5}
					hoverKeyboardStep={1}
					hoverShiftKeyboardStep={10}
					onUpdate={(val) => handleUpdateValue("lumShift", val)}
					onUpdateEnded={(_, displayedValue) =>
						handleCommitValue("lumShift", displayedValue)
					}
				/>
				<div className="my-2 border-b border-base-900" />
				<ControlGroup
					isExpanded={false}
					name=""
					expandKey="hsl_point_color_ranges"
					className="px-0"
					hideSeparator={true}
					fullSizeContent={true}
					compact={true}
					customExpandView={renderRangeSliderExpandView}
					customNameView={renderRangeSlider()}
				>
					<h4 className="mt-2 text-xs text-base-300">{t("hslPointColor.hueRange")}</h4>
					<HardSoftRangeSlider
						disabled={selectedPointColor === null}
						onSliderDown={props.onSliderDown}
						onSliderUp={props.onSliderUp}
						initial={getCurrentPointColorRelativeHSV()[0]}
						range={selectedPointColor?.hueRange ?? [0, 0.3, 0.6, 1.0]}
						onUpdate={(val) => handleUpdateValue("hueRange", val)}
						onUpdateEnded={(displayedValue) =>
							handleCommitValue("hueRange", displayedValue)
						}
						background={getHueBackground(
							getCurrentPointColorHSV(),
							PointColorHueMaxShift,
						)}
					/>

					<h4 className="mt-2 text-xs text-base-300">
						{t("hslPointColor.saturationRange")}
					</h4>
					<HardSoftRangeSlider
						disabled={selectedPointColor === null}
						onSliderDown={props.onSliderDown}
						onSliderUp={props.onSliderUp}
						initial={getCurrentPointColorRelativeHSV()[1]}
						range={selectedPointColor?.satRange ?? [0, 0.3, 0.6, 1.0]}
						onUpdate={(val) => handleUpdateValue("satRange", val)}
						onUpdateEnded={(displayedValue) =>
							handleCommitValue("satRange", displayedValue)
						}
						background={getSatBackground(
							getCurrentPointColorHSV(),
							PointColorSatMaxShift,
						)}
					/>

					<h4 className="mt-2 text-xs text-base-300">
						{t("hslPointColor.luminanceRange")}
					</h4>
					<HardSoftRangeSlider
						disabled={selectedPointColor === null}
						onSliderDown={props.onSliderDown}
						onSliderUp={props.onSliderUp}
						initial={getCurrentPointColorRelativeHSV()[2]}
						range={selectedPointColor?.lumRange ?? [0, 0.3, 0.6, 1.0]}
						onUpdate={(val) => handleUpdateValue("lumRange", val)}
						onUpdateEnded={(displayedValue) =>
							handleCommitValue("lumRange", displayedValue)
						}
						background={getLumBackground(
							getCurrentPointColorHSV(),
							PointColorLumMaxShift,
						)}
					/>
				</ControlGroup>
			</div>
		</div>
	)
}
